/**
 * The vocabulary of the voice-profile panel, in one place.
 *
 * Two ideas the panel has to make legible and that nothing else in the dashboard expresses:
 *
 *   1. A profile the pipeline discovered on its own is *not* an identity yet. It matches, it
 *      collects clips, and it is called `Voz 3`. Naming it is the work.
 *   2. Nothing an editor presses takes effect immediately. The training audio and the voice
 *      encoder live on the pipeline box, so an edit is queued and applied on its next run. A
 *      panel that showed a rename as done would be lying about the state of the system.
 */

import type { EditorialSpeakerCommandType, EditorialSpeakerCommandTypeInternal, EditorialSpeakerProfileStatus } from 'src/types/editorial';
import type { Tone } from 'src/lib/tone';

export const PROFILE_STATUS_LABEL = {
  active: 'Identificada',
  pending_label: 'Sin nombrar',
  archived: 'Archivada',
} satisfies Record<EditorialSpeakerProfileStatus, string>;

export const PROFILE_STATUS_MEANING = {
  active: 'Alguien confirmó de quién es esta voz. Se usa para etiquetar hablantes en las transcripciones.',
  pending_label:
    'El sistema detectó una voz que no reconocía y le puso un nombre provisional. Ya se usa para agrupar segmentos, pero nadie ha dicho quién es. Escucha los clips y ponle nombre.',
  archived: 'Fuera de uso. No se compara contra ella al transcribir, pero sus clips se conservan.',
} satisfies Record<EditorialSpeakerProfileStatus, string>;

/** Art Direction §8: a profile's state is not a verification verdict and never borrows its color. */
export const PROFILE_STATUS_TONE = {
  active: 'info',
  pending_label: 'partial',
  archived: 'unresolved',
} satisfies Record<EditorialSpeakerProfileStatus, Tone>;

export const COMMAND_LABEL = {
  rename_profile: 'Renombrar',
  set_clip_included: 'Incluir o excluir un clip',
  recompute_centroid: 'Recalcular huella de voz',
  archive_profile: 'Archivar',
  merge_profiles: 'Fusionar con otra voz',
  create_profile: 'Crear la voz',
  add_uploaded_clip: 'Agregar audio subido',
} satisfies Record<EditorialSpeakerCommandType | EditorialSpeakerCommandTypeInternal, string>;

export const COMMAND_STATUS_LABEL: Record<string, string> = {
  pending: 'En cola',
  running: 'Aplicándose',
  done: 'Aplicado',
  failed: 'Falló',
};

export const COMMAND_STATUS_TONE: Record<string, Tone> = {
  pending: 'unresolved',
  running: 'partial',
  done: 'supported',
  failed: 'contradicted',
};

/**
 * How much a profile can be trusted, stated in words rather than as a bare clip count.
 *
 * The thresholds are judgment, not measurement — they exist so that "3 clips" and "80 clips" do
 * not read as the same kind of profile in a list. `hasCentroid` comes first because a profile
 * with every clip excluded matches nothing at all, whatever its counts say.
 */
export type ProfileStrength = {
  level: 'none' | 'weak' | 'usable' | 'solid';
  label: string;
  advice: string;
};

export function profileStrength(profile: { clipCount: number; hasCentroid: boolean }): ProfileStrength {
  if (!profile.hasCentroid || profile.clipCount === 0) {
    return {
      level: 'none',
      label: 'Sin huella de voz',
      advice: 'No tiene clips incluidos, así que no reconoce a nadie. Vuelve a incluir algún clip o archívala.',
    };
  }
  if (profile.clipCount < 5) {
    return {
      level: 'weak',
      label: 'Huella débil',
      advice: 'Pocos clips: puede confundirse con voces parecidas. Mejora sola conforme aparezca en más streams.',
    };
  }
  if (profile.clipCount < 20) {
    return {
      level: 'usable',
      label: 'Huella utilizable',
      advice: 'Suficiente para reconocer la voz en condiciones parecidas de audio.',
    };
  }
  return {
    level: 'solid',
    label: 'Huella sólida',
    advice: 'Muchos clips y probablemente de varias sesiones: resiste cambios de micrófono y de sala.',
  };
}

/** `Voz 7` from the pipeline, or a real name an editor already wrote. */
export function isUnnamed(profile: { status: EditorialSpeakerProfileStatus }): boolean {
  return profile.status === 'pending_label';
}

/** Unnamed voices first — labeling them is the panel's job — then by how much they are used. */
export function sortProfiles<T extends { status: EditorialSpeakerProfileStatus; matchCount: number; clipCount: number; name: string }>(profiles: T[]): T[] {
  const rank = { pending_label: 0, active: 1, archived: 2 } satisfies Record<EditorialSpeakerProfileStatus, number>;
  return [...profiles].sort((a, b) => {
    if (rank[a.status] !== rank[b.status]) return rank[a.status] - rank[b.status];
    if (b.matchCount !== a.matchCount) return b.matchCount - a.matchCount;
    if (b.clipCount !== a.clipCount) return b.clipCount - a.clipCount;
    return a.name.localeCompare(b.name, 'es');
  });
}

/** Seconds to `m:ss`, for a clip's position inside its stream. */
export function formatClipOffset(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
}
